import React from 'react'
import {
  Menu,
  MenuItem,
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Badge,
  Avatar,
} from '@mui/material'
import {
  Close,
  Info,
  Warning,
  Error,
  CheckCircle,
  MarkEmailRead,
  ClearAll,
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'

interface Notification {
  id: string
  type: 'info' | 'warning' | 'error' | 'success'
  title: string
  message: string
  timestamp: Date
  read: boolean
}

interface NotificationPanelProps {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  notifications: Notification[]
  onMarkAsRead?: (id: string) => void
  onMarkAllAsRead?: () => void
  onClear?: () => void
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'error':
      return <Error color="error" />
    case 'warning':
      return <Warning color="warning" />
    case 'success':
      return <CheckCircle color="success" />
    default:
      return <Info color="info" />
  }
}

const getNotificationColor = (type: string) => {
  switch (type) {
    case 'error':
      return 'error.main'
    case 'warning':
      return 'warning.main'
    case 'success':
      return 'success.main'
    default:
      return 'info.main'
  }
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  anchorEl,
  open,
  onClose,
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onClear,
}) => {
  const unreadCount = notifications.filter(n => !n.read).length

  const handleMarkAsRead = (id: string) => {
    onMarkAsRead?.(id)
  }

  const handleMarkAllAsRead = () => {
    onMarkAllAsRead?.()
  }

  const handleClear = () => {
    onClear?.()
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          width: 400,
          maxHeight: 500,
          backgroundColor: 'surface',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'outlineVariant',
          boxShadow: 3,
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 3,
        borderBottom: '1px solid',
        borderColor: 'outlineVariant',
        backgroundColor: 'surfaceContainer',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ color: 'onSurface', fontWeight: 500 }}>
            Notifications
            {unreadCount > 0 && (
              <Badge
                badgeContent={unreadCount}
                color="error"
                sx={{
                  ml: 1,
                  '& .MuiBadge-badge': {
                    backgroundColor: 'error.main',
                    color: 'onError',
                  }
                }}
              />
            )}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: 'onSurfaceVariant',
              '&:hover': {
                backgroundColor: 'surfaceVariant',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
        
        {notifications.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                startIcon={<MarkEmailRead />}
                onClick={handleMarkAllAsRead}
                sx={{
                  borderRadius: 5,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  color: 'onSurface',
                  '&:hover': {
                    backgroundColor: 'surfaceVariant',
                  },
                }}
              >
                Mark all read
              </Button>
            )}
            <Button
              size="small"
              startIcon={<ClearAll />}
              onClick={handleClear}
              sx={{
                borderRadius: 5,
                textTransform: 'none',
                fontSize: '0.875rem',
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'errorContainer',
                  color: 'onErrorContainer',
                },
              }}
            >
              Clear all
            </Button>
          </Box>
        )}
      </Box>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', backgroundColor: 'surface' }}>
          <Typography variant="body2" color="onSurfaceVariant">
            No notifications
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0, maxHeight: 350, overflow: 'auto', backgroundColor: 'surface' }}>
          {notifications.map((notification, index) => (
            <React.Fragment key={notification.id}>
              <ListItem
                sx={{
                  bgcolor: notification.read ? 'transparent' : 'surfaceVariant',
                  cursor: 'pointer',
                  py: 2,
                  px: 3,
                  '&:hover': {
                    bgcolor: 'surfaceContainer',
                  },
                }}
                onClick={() => handleMarkAsRead(notification.id)}
              >
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: getNotificationColor(notification.type),
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.25rem',
                      },
                    }}
                  >
                    {getNotificationIcon(notification.type)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: notification.read ? 400 : 500,
                          flex: 1,
                          color: 'onSurface',
                          fontSize: '1rem',
                        }}
                      >
                        {notification.title}
                      </Typography>
                      {!notification.read && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography
                        variant="body2"
                        color="onSurfaceVariant"
                        sx={{ mb: 0.5, lineHeight: 1.4 }}
                      >
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="onSurfaceVariant" sx={{ fontSize: '0.75rem' }}>
                        {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              {index < notifications.length - 1 && (
                <Divider sx={{ borderColor: 'outlineVariant', mx: 3 }} />
              )}
            </React.Fragment>
          ))}
        </List>
      )}
    </Menu>
  )
}
